import BigNumber from 'bignumber.js'

export const WIND_DIRECTION_LABELS = {
  '00': '静穏',
  '01': '北北東',
  '02': '北東',
  '03': '東北東',
  '04': '東',
  '05': '東南東',
  '06': '南東',
  '07': '南南東',
  '08': '南',
  '09': '南南西',
  '10': '南西',
  '11': '西南西',
  '12': '西',
  '13': '西北西',
  '14': '北西',
  '15': '北北西',
  '16': '北',
} as const

export const windDirectionLabel = (direction?: number) => {
  if (direction === undefined) {
    return ''
  }

  const directionKey = direction.toString().padStart(2, '0') as keyof typeof WIND_DIRECTION_LABELS

  return WIND_DIRECTION_LABELS[directionKey]
}

export const API_CONFIG = {
  responsType: 'json',
}

export const API_SECRET = process.env.API_SECRET || 'secret'

export const ON_OFF = {
  ON: 1,
  OFF: 9,
} as const

export type ON_OFF_TYPE = (typeof ON_OFF)[keyof typeof ON_OFF]

export const VIEW_MODES = {
  DARK: 1,
  LIGHT: 2,
} as const

export type ViewMode = (typeof VIEW_MODES)[keyof typeof VIEW_MODES]

export const STATUSES = {
  OK: 'OK',
  NG: 'NG',
} as const

export const ALERT_TYPE = {
  WARNING: 'warning', // 注意報
  ALERT: 'alert', // 警報
  SPECIAL: 'special', // 特別警報
}

export const ALERT_VALUES = {
  [ALERT_TYPE.WARNING]: 1,
  [ALERT_TYPE.ALERT]: 2,
  [ALERT_TYPE.SPECIAL]: 3,
} as const

export type AlertLevel = (typeof ALERT_VALUES)[keyof typeof ALERT_VALUES]

export const ALERT_TYPE_TEXTS = {
  [ALERT_VALUES[ALERT_TYPE.WARNING]]: '注意報',
  [ALERT_VALUES[ALERT_TYPE.ALERT]]: '警報',
  [ALERT_VALUES[ALERT_TYPE.SPECIAL]]: '特別警報',
}

export const TARGET_ELEMENTS = {
  RAINFALL_10MIN: 'rainfall_10min',
  RAINFALL_HOUR: 'rainfall_hour',
} as const

export const TARGET_ELEMENT_TEXTS = {
  [TARGET_ELEMENTS.RAINFALL_10MIN]: '10分降水量',
  [TARGET_ELEMENTS.RAINFALL_HOUR]: '1時間降水量',
}

export type RainfallLevel = 0 | 1 | 2 | 3

export const RAINFALL_LEVEL_TEXT = {
  0: '無',
  1: '弱',
  2: '中',
  3: '強',
}

export type TargetElement = (typeof TARGET_ELEMENTS)[keyof typeof TARGET_ELEMENTS]

export const KNOT_PER_MS = new BigNumber(3600).div(1852)

export const HEADER_SIZE = 68
export const HEADER_MARGIN = 8
export const CONTROLLER_SIZE = 38
export const TABLE_MARGIN = 32
export const TABLE_HEADER_SIZE = 60
export const SUMMARY_SIZE = 90

export const RESOLUTIONS = {
  TEN_MIN: '10min',
  HOURLY: 'hourly',
  DAILY: 'daily',
}

export const TIME_UNITS = {
  [RESOLUTIONS.TEN_MIN]: 'minutes',
  [RESOLUTIONS.HOURLY]: 'hour',
  [RESOLUTIONS.DAILY]: 'day',
}

export type Resolution = (typeof RESOLUTIONS)[keyof typeof RESOLUTIONS]

export const MAP_CONST = {
  CENTER: {
    LATITUDE: 35.586261,
    LONGITUDE: 139.608049,
    ZOOM: 12,
  },
  RANGE: {
    LEFT_UP: {
      latitude: 36.1,
      longitude: 138.5,
    },
    RIGHT_DOWN: {
      latitude: 34.6,
      longitude: 141.05,
    },
    MIN_ZOOM: 10,
  },
}

export type WeatherWarningHistoryDicItem = {
  code: string
  specialCode?: string
  name: string
  type: string
}

export const WEATHER_WARNINGS = [
  {
    code: '02',
    specialCode: '32',
    name: '暴風雪',
    type: 'specia',
  },
  {
    code: '03',
    specialCode: '33',
    name: '大雨',
    type: 'specia',
  },
  {
    code: '04',
    name: '洪水',
    type: 'specia',
  },
  {
    code: '05',
    specialCode: '35',
    name: '暴風',
    type: 'specia',
  },
  {
    code: '06',
    specialCode: '36',
    name: '大雪',
    type: 'specia',
  },
  {
    code: '07',
    specialCode: '37',
    name: '波浪',
    type: 'specia',
  },
  {
    code: '08',
    specialCode: '38',
    name: '高潮',
    type: 'specia',
  },
  {
    code: '10',
    name: '大雨',
    type: 'warn',
  },
  {
    code: '12',
    name: '大雪',
    type: 'warn',
  },
  {
    code: '13',
    name: '風雪',
    type: 'warn',
  },
  {
    code: '14',
    name: '雷',
    type: 'warn',
  },
  {
    code: '15',
    name: '強風',
    type: 'warn',
  },
  {
    code: '16',
    name: '波浪',
    type: 'warn',
  },
  {
    code: '17',
    name: '融雪',
    type: 'warn',
  },
  {
    code: '18',
    name: '洪水',
    type: 'warn',
  },
  {
    code: '19',
    name: '高潮',
    type: 'warn',
  },
  {
    code: '20',
    name: '濃霧',
    type: 'warn',
  },
  {
    code: '21',
    name: '乾燥',
    type: 'warn',
  },
  {
    code: '22',
    name: 'なだれ',
    type: 'warn',
  },
  {
    code: '23',
    name: '低温',
    type: 'warn',
  },
  {
    code: '24',
    name: '霜',
    type: 'warn',
  },
  {
    code: '25',
    name: '着氷',
    type: 'warn',
  },
  {
    code: '26',
    name: '着雪',
    type: 'warn',
  },
]

export const WEATHER_FIELDS = [
  { value: 'moveWsMin10', snakeCaseValue: 'move_ws_min_10', label: '10分平均風速', unit: 'm/s' },
  { value: 'moveWdMin10', snakeCaseValue: 'move_wd_min_10', label: '10分平均風向', unit: '' },
  { value: 'moveMaxWsMin10', snakeCaseValue: 'move_max_ws_min_10', label: '10分最大風速', unit: 'm/s' },
  { value: 'moveMaxWdMin10', snakeCaseValue: 'move_max_wd_min_10', label: '10分最大風向', unit: '' },
  { value: 'temp', snakeCaseValue: 'temp', label: '気温', unit: '℃' },
  { value: 'humid1', snakeCaseValue: 'humid1', label: '湿度', unit: '%' },
  { value: 'press1', snakeCaseValue: 'press1', label: '現地気圧', unit: 'hPa' },
  { value: 'moveRainMin10', snakeCaseValue: 'move_rain_min_10', label: '10分雨量', unit: 'mm' },
  { value: 'moveRainMin60', snakeCaseValue: 'move_rain_min_60', label: '時間積算', unit: 'mm' },
  { value: 'rainRui', snakeCaseValue: 'rain_rui', label: '累計雨量', unit: 'mm' },
  { value: 'rainfall', snakeCaseValue: 'rainfall', label: '降雨強度', unit: 'mm/h' },
]

export default {
  WIND_DIRECTION_LABELS,
  windDirectionLabel,
  API_CONFIG,
  API_SECRET,
  ON_OFF,
  ALERT_TYPE,
  ALERT_TYPE_TEXTS,
  ALERT_VALUES,
  KNOT_PER_MS,
  VIEW_MODES,
  MAP_CONST,
}
